import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { useStore } from "@nanostores/react";
import clsx from "clsx";
import { PropsWithChildren, ReactElement, useMemo } from "react";

import { SidebarItem } from "./catalyst/SidebarItem";
import {
  getPersistentBooleanAtom,
  useSidebarBehavior,
} from "./collapsible-sidebar-behavior";

export const SidebarGroup = ({
  children,
  defaultOpen = true,
  icon,
  label,
  storageKey,
}: {
  defaultOpen?: boolean;
  icon: React.ReactNode;
  label: string;
  storageKey?: string;
} & PropsWithChildren): ReactElement => {
  const { collapsed, itemTitle, labelClassName } = useSidebarBehavior();

  const $open = useMemo(
    () =>
      getPersistentBooleanAtom(
        storageKey ?? `mvz.sidebar.group.${label}.open`,
        defaultOpen,
      ),
    [defaultOpen, label, storageKey],
  );
  const persistedOpen = useStore($open);

  const title = collapsed ? itemTitle(label) : undefined;

  return (
    <Disclosure defaultOpen={persistedOpen}>
      {({ open }) => (
        <div>
          <DisclosureButton
            as={SidebarItem}
            onClick={() => $open.set(!open)}
            title={title}
          >
            {icon}

            <span className={clsx("flex-1 truncate text-sm", labelClassName)}>
              {label}
            </span>

            <ChevronDownIcon
              className={clsx(collapsed && "hidden", open && "rotate-180")}
              data-slot="icon"
            />
          </DisclosureButton>

          <DisclosurePanel className={clsx(collapsed ? "hidden" : "mt-1 pl-9")}>
            {children}
          </DisclosurePanel>
        </div>
      )}
    </Disclosure>
  );
};
